import { useCallback, useEffect, useReducer } from 'react';

import { dateUtils } from 'shared_DEPRECATED/utils';

import { mapAudioAttachment } from 'features/chat/utils/audioAttachment';

import { isMicPermissionsDenied } from 'shared/utils/permissions';

import { useAudioRecorder } from './useAudioRecorder';

const AUDIO_FILE_NAME = 'audio-file';
const AUDIO_FILE_EXT = 'm4a';

const initialState = {
  recordButtonVisible: true,
  textInputVisible: true,
  micPermissionsDenied: false,
  isRecording: false,
  isRecordingPaused: false,
  isTextMode: true,
};

export const useChatFormOrchestrator = ({ hasText }: { hasText: boolean }) => {
  const [state, dispatch] = useReducer(chatFormReducer, initialState);

  const {
    getLocalAudioTrack,
    startAudioRecording,
    stopAudioRecording,
    cancelAudioRecording,
    mediaStream,
    getRecordingInfo,
  } = useAudioRecorder({
    isPaused: state.isRecordingPaused,
  });

  const startRecording = useCallback(() => {
    startAudioRecording();
    dispatch({ type: 'START_RECORDING' });
  }, [startAudioRecording]);

  const pauseRecording = useCallback(() => {
    dispatch({ type: 'PAUSE_RECORDING' });
  }, []);

  useEffect(() => {
    if (mediaStream) {
      startRecording();
    }
  }, [startRecording, mediaStream]);

  const stopRecording = useCallback(async () => {
    const { duration, blob } = await stopAudioRecording();
    dispatch({ type: 'STOP_RECORDING' });

    const file = new File([blob as Blob], composeAudioFileName(), {
      type: blob?.type,
    });

    return mapAudioAttachment({
      file,
      duration,
    });
  }, [stopAudioRecording]);

  const cancelRecording = useCallback(() => {
    cancelAudioRecording();
    dispatch({ type: 'STOP_RECORDING' });
  }, [cancelAudioRecording]);

  const initiateRecording = async () => {
    const isMicPermissionDenied = await isMicPermissionsDenied();
    if (isMicPermissionDenied) {
      dispatch({
        type: 'SET_MIC_PERMISSION_DENIED',
        payload: isMicPermissionDenied,
      });
    } else {
      try {
        await getLocalAudioTrack();
      } catch (error) {
        dispatch({
          type: 'SET_MIC_PERMISSION_DENIED',
          payload: true,
        });
      }
    }
  };

  const getRecordedAttachment = () => {
    const { duration, blob } = getRecordingInfo();
    const file = new File([blob], composeAudioFileName(), {
      type: blob?.type,
    });

    return mapAudioAttachment({
      file,
      duration,
    });
  };

  const setHasText = (hasText: boolean) =>
    dispatch({ type: 'SET_HAS_TEXT', payload: hasText });

  useEffect(() => {
    setHasText(hasText);
  }, [hasText]);

  return {
    ...state,
    stopRecording,
    cancelRecording,
    initiateRecording,
    pauseRecording,
    getRecordedAttachment,
  };
};

export type ChatFormState = {
  recordButtonVisible: boolean;
  textInputVisible: boolean;
  micPermissionsDenied: boolean;
  isRecording: boolean;
  isRecordingPaused: boolean;
  isTextMode: boolean;
};
type START_RECORDING = { type: 'START_RECORDING'; payload?: any };
type STOP_RECORDING = { type: 'STOP_RECORDING'; payload?: any };
type PAUSE_RECORDING = { type: 'PAUSE_RECORDING'; payload?: any };
type SET_HAS_TEXT = { type: 'SET_HAS_TEXT'; payload: boolean };
type SET_MIC_PERMISSION_DENIED = {
  type: 'SET_MIC_PERMISSION_DENIED';
  payload: boolean;
};

export type ChatFormAction =
  | START_RECORDING
  | STOP_RECORDING
  | SET_HAS_TEXT
  | PAUSE_RECORDING
  | SET_MIC_PERMISSION_DENIED;

export function chatFormReducer(state: ChatFormState, action: ChatFormAction) {
  const { type, payload } = action;

  switch (type) {
    case 'START_RECORDING':
      return {
        ...state,
        isTextMode: false,
        isRecording: true,
        textInputVisible: false,
        recordButtonVisible: false,
      };
    case 'STOP_RECORDING':
      return { ...initialState };
    case 'SET_HAS_TEXT':
      return {
        ...state,
        recordButtonVisible: !payload,
      };
    case 'SET_MIC_PERMISSION_DENIED':
      return {
        ...state,
        micPermissionsDenied: payload,
      };
    case 'PAUSE_RECORDING':
      return {
        ...state,
        isRecording: false,
        textInputVisible: false,
        isRecordingPaused: true,
      };
    default:
      return state;
  }
}

const composeAudioFileName = () =>
  `${AUDIO_FILE_NAME}-${dateUtils().unixTimestamp()}.${AUDIO_FILE_EXT}`;
